import { LocaleLink } from "@/components/LocaleLink";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { useLocale } from "@/lib/i18n";
import { getPostsByTheme } from "@/lib/blog-posts";
import { blogThemes, type BlogTheme } from "@/lib/blog-themes";
import { breadcrumbJsonLd, localizedHead, SITE_URL } from "@/lib/seo";

export function buildBlogThemeHead(theme: BlogTheme, locale: "da" | "en") {
  const c = theme[locale];
  const path = `/blog/${theme.id}`;
  const prefix = locale === "en" ? "/en" : "";
  const loc = localizedHead({ path, locale });
  const crumb = breadcrumbJsonLd([
    { name: locale === "en" ? "Home" : "Forsiden", url: SITE_URL },
    { name: "Guides", url: `${SITE_URL}${prefix}/blog` },
    { name: c.title, url: `${SITE_URL}${prefix}${path}` },
  ]);
  const image = theme.image.startsWith("http") ? theme.image : `${SITE_URL}${theme.image}`;
  return {
    meta: [
      { title: c.metaTitle },
      { name: "description", content: c.metaDescription },
      { property: "og:title", content: c.metaTitle },
      { property: "og:description", content: c.metaDescription },
      { property: "og:image", content: image },
      ...loc.meta,
    ],
    links: loc.links,
    scripts: [{ type: "application/ld+json", children: JSON.stringify(crumb) }],
  };
}

export function BlogThemePage({ theme }: { theme: BlogTheme }) {
  const locale = useLocale();
  const c = theme[locale];
  const posts = getPostsByTheme(theme.id);
  const others = blogThemes.filter((t) => t.id !== theme.id);
  const backLabel = locale === "da" ? "← Alle guides" : "← All guides";
  const countLabel = `${posts.length} ${posts.length === 1 ? "guide" : "guides"}`;
  const emptyTitle = locale === "da" ? "Guides på vej" : "Guides coming soon";
  const emptyBody =
    locale === "da"
      ? "Vi skriver på de første guides til dette tema. Kig forbi igen snart — eller find inspiration i et af de andre temaer herunder."
      : "We are writing the first guides for this theme. Check back soon — or find inspiration in one of the other themes below.";
  const othersTitle = locale === "da" ? "Andre temaer" : "Other themes";

  return (
    <>
      <section className="pt-40 pb-12 md:pt-52 md:pb-16">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <LocaleLink
            to="/blog"
            className="text-xs font-medium uppercase tracking-[0.22em] text-foreground/60 hover:text-terracotta"
          >
            {backLabel}
          </LocaleLink>
          <p className="eyebrow mt-10">Guides · {c.label}</p>
          <h1 className="mt-6 max-w-4xl font-display text-[clamp(3rem,7vw,6rem)] leading-[0.95] text-balance">
            {c.title}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/75">{c.intro}</p>
          {posts.length > 0 && (
            <p className="mt-6 text-xs uppercase tracking-[0.22em] text-muted-foreground">
              {countLabel}
            </p>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-24 md:px-10 md:pb-32">
        {posts.length > 0 ? (
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-10 lg:gap-y-16">
            {posts.map((post) => (
              <BlogPostCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <div className="border-y border-border py-20 text-center">
            <p className="font-display text-2xl text-foreground/80">{emptyTitle}</p>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              {emptyBody}
            </p>
          </div>
        )}
      </section>

      <section className="border-t border-border/60">
        <div className="mx-auto max-w-[1500px] px-6 py-20 md:px-10 md:py-24">
          <h2 className="font-display text-2xl text-foreground md:text-3xl">{othersTitle}</h2>
          <div className="mt-8 grid gap-8 sm:grid-cols-3">
            {others.map((t) => (
              <LocaleLink
                key={t.id}
                to="/blog/$slug"
                params={{ slug: t.id }}
                className="group block"
              >
                <div className="aspect-[16/10] overflow-hidden rounded-sm bg-muted">
                  <img
                    src={t.image}
                    alt={t.imageAlt[locale]}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <p className="mt-4 font-display text-xl leading-tight text-foreground transition-colors group-hover:text-terracotta">
                  {t[locale].title} <span aria-hidden>→</span>
                </p>
              </LocaleLink>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
